import PropTypes from "prop-types"
import { useState } from "react"
import { Download, FileText, FileSpreadsheet } from "../Icon"
import AppDropdownMenu from "../DropdownMenu/AppDropdownMenu"
import {
  exportTableToPdf,
  exportTableToExcel,
} from "../../../helpers/tableExport"
import { Button } from "../"
import { useCanExport } from "../../../hooks/useCanExport"

/**
 * Reusable Export dropdown button. Replaces separate Print + Export buttons.
 *
 * @param {Object}     props
 * @param {string}     props.title           — Report title for the document header
 * @param {string}     props.filename        — Output filename without extension
 * @param {string[]}   props.headers         — Column header labels
 * @param {string[][]} props.rows            — 2D array of string values (already filtered)
 * @param {string}     [props.filterSummary] — Human-readable filter description
 * @param {string}     [props.orientation]   — "portrait" | "landscape" (default: auto)
 * @param {string[]}   [props.formats]       — Formats to show: ["pdf", "excel"]. Defaults to both.
 *                                             Pass ["pdf"] for profile/detail pages that don't need spreadsheet export.
 * @param {string}     [props.className]     — Additional CSS class for the trigger button
 */
export default function ExportMenu({
  title,
  filename,
  headers,
  rows,
  filterSummary = "",
  orientation,
  formats = ["pdf", "excel"],
  className = "",
}) {
  const canExport = useCanExport()
  const [exporting, setExporting] = useState(false)
  // Read-Only Admin: one gate here covers every table that mounts ExportMenu.
  if (!canExport) return null

  const handleExport = async (format) => {
    if (exporting) return
    setExporting(true)
    try {
      const opts = {
        title,
        filename,
        headers,
        rows,
        filterSummary,
        orientation,
      }
      if (format === "pdf") {
        await exportTableToPdf(opts)
      } else {
        await exportTableToExcel(opts)
      }
    } finally {
      setExporting(false)
    }
  }

  const trigger = (
    <Button
      variant="outline"
      size="sm"
      className={className || undefined}
      disabled={exporting || !rows || rows.length === 0}
      aria-label="Export"
      data-testid="export-menu-trigger"
    >
      <Download size={16} />
      {exporting ? "Exporting..." : "Export"}
    </Button>
  )

  const allItems = [
    {
      key: "pdf",
      label: "Export as PDF",
      icon: <FileText size={14} />,
      onClick: () => handleExport("pdf"),
      disabled: exporting || !rows || rows.length === 0,
      "data-testid": "export-pdf",
    },
    {
      key: "excel",
      label: "Export as Excel",
      icon: <FileSpreadsheet size={14} />,
      onClick: () => handleExport("xlsx"),
      disabled: exporting || !rows || rows.length === 0,
      "data-testid": "export-excel",
    },
  ]
  const items = allItems.filter((item) => formats.includes(item.key))

  return <AppDropdownMenu trigger={trigger} items={items} />
}

ExportMenu.propTypes = {
  title: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired,
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  filterSummary: PropTypes.string,
  orientation: PropTypes.string,
  formats: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
}
